import { Controller } from "@hotwired/stimulus"

/**
 * OmniSearch Controller
 *
 * Central search/assistant UI on the home page.
 * - Integrated mode selector dropdown (grouped: Asistente / Buscar / Tu organización)
 * - Typewriter placeholder cycling through examples
 * - 2-level example navigation: group tabs → chips (subcategories or flat items)
 * - Optional 3rd level: clicking a subcategory chip opens a floating dropdown with concrete examples
 * - Routes submission to assistant, global search, or section navigation
 */
export default class extends Controller {
  static targets = [
    "input", "submitIcon",
    "dropdownContainer", "dropdownTrigger", "dropdownMenu", "dropdownChevron", "dropdownItem",
    "modeIcon", "modeLabel",
    "groupTabs", "examplesContainer", "examplesInner", "specialistsList",
    "exampleDropdown", "exampleDropdownItems",
    "assistantForm", "assistantInput", "assistantSpecialistId",
    "modelSelect", "retrievalStrategySelect", "reasoningEffortSelect",
    "chipChevronTemplate", "specialistIconTemplate"
  ]

  static values = {
    modes: Array,
    searchPath: String,
    currentMode: { type: String, default: "assistant" },
    labelTryWith: { type: String, default: "Prueba con" },
    labelExample: { type: String, default: "Ej." }
  }

  connect() {
    this.activeGroupIndex = 0
    this.dropdownOpen = false
    this.exampleDropdownOpen = false
    this.updateDropdownItems()
    this.renderGroupTabs()
    this.renderChips()
    this.startTypingAnimation()

    // Close example dropdown on outside click
    this.boundCloseExampleDropdown = (e) => {
      if (this.exampleDropdownOpen && !e.target.closest("[data-omni-search-target='examplesContainer']")) {
        this.closeExampleDropdown()
      }
    }
    document.addEventListener("click", this.boundCloseExampleDropdown)

    // Auto-focus input when in assistant mode (e.g. /asistente page)
    if (this.currentModeValue === "assistant") {
      this.inputTarget.focus()
    }
  }

  disconnect() {
    this.stopTypingAnimation()
    document.removeEventListener("click", this.boundCloseExampleDropdown)
  }

  // ─── Mode selector dropdown ──────────────────────────────

  toggleDropdown(event) {
    if (!this.hasDropdownMenuTarget) return
    event.stopPropagation()
    this.dropdownOpen ? this.closeDropdown() : this.openDropdown()
  }

  openDropdown() {
    if (!this.hasDropdownMenuTarget) return
    this.dropdownOpen = true
    this.dropdownMenuTarget.classList.remove("hidden")
    this.dropdownChevronTarget.classList.add("rotate-180")
  }

  closeDropdown() {
    if (!this.dropdownOpen || !this.hasDropdownMenuTarget) return
    this.dropdownOpen = false
    this.dropdownMenuTarget.classList.add("hidden")
    this.dropdownChevronTarget.classList.remove("rotate-180")
  }

  selectMode(event) {
    event.stopPropagation()
    const modeId = event.currentTarget.dataset.modeId
    if (modeId === this.currentModeValue) {
      this.closeDropdown()
      return
    }

    this.currentModeValue = modeId
    this.activeGroupIndex = 0
    const mode = this.getCurrentMode()

    if (this.hasModeLabelTarget) this.modeLabelTarget.textContent = mode.label
    const sourceItem = this.dropdownItemTargets.find(el => el.dataset.modeId === modeId)
    if (sourceItem && this.hasModeIconTarget) {
      this.modeIconTarget.innerHTML = sourceItem.querySelector("span:first-child")?.innerHTML || ""
    }

    this.updateDropdownItems()
    this.renderGroupTabs()
    this.renderChips()
    this.closeExampleDropdown()
    this.resetTypingAnimation()
    this.closeDropdown()
    this.setInputValue("")
    this.inputTarget.focus()
    this.updateSubmitIcon()
    this.updateSlashCommandState(mode)
  }

  updateDropdownItems() {
    if (!this.hasDropdownItemTarget) return
    this.dropdownItemTargets.forEach(item => {
      const isActive = item.dataset.modeId === this.currentModeValue
      item.classList.toggle("omni-dropdown-item--active", isActive)
    })
  }

  // ─── Level 1: Group tabs ──────────────────────────────────

  renderGroupTabs() {
    const mode = this.getCurrentMode()
    const groups = mode?.examples || []
    const container = this.groupTabsTarget
    container.innerHTML = ""

    if (groups.length <= 1) {
      container.classList.add("hidden")
      return
    }

    container.classList.remove("hidden")
    groups.forEach((group, index) => {
      const tab = document.createElement("button")
      tab.type = "button"
      tab.dataset.action = "click->omni-search#selectGroup"
      tab.dataset.groupIndex = index

      if (group.specialists) {
        const iconHtml = this.hasSpecialistIconTemplateTarget
          ? this.specialistIconTemplateTarget.innerHTML : ""
        tab.className = "omni-group-tab--specialist"
        tab.innerHTML = `<span class="text-gobyellow">${iconHtml}</span> ${this.escapeHtml(group.group)}`
        if (index === this.activeGroupIndex) tab.classList.add("omni-group-tab--specialist-active")
      } else {
        tab.className = "omni-group-tab"
        tab.textContent = group.group
        if (index === this.activeGroupIndex) tab.classList.add("omni-group-tab--active")
      }

      container.appendChild(tab)
    })
  }

  selectGroup(event) {
    const index = parseInt(event.currentTarget.dataset.groupIndex, 10)
    if (index === this.activeGroupIndex) return

    this.activeGroupIndex = index
    this.closeExampleDropdown()

    this.groupTabsTarget.querySelectorAll("[data-group-index]").forEach((tab, i) => {
      const isSpecialist = tab.classList.contains("omni-group-tab--specialist") ||
                           tab.classList.contains("omni-group-tab--specialist-active")
      if (isSpecialist) {
        tab.classList.toggle("omni-group-tab--specialist-active", i === index)
        tab.classList.toggle("omni-group-tab--specialist", i !== index)
      } else {
        tab.classList.toggle("omni-group-tab--active", i === index)
      }
    })

    this.crossfadeChips()
  }

  // ─── Level 2: Chips (subcategories or flat items) ─────────

  renderChips() {
    const group = this.getActiveGroup()
    const inner = this.examplesInnerTarget
    const list = this.hasSpecialistsListTarget ? this.specialistsListTarget : null

    inner.innerHTML = ""

    // Specialists group → show the server-rendered list (same as plus-menu),
    // hide the chip container. For any other group, the chip container is used.
    const showList = !!group?.specialists
    if (list) list.classList.toggle("hidden", !showList)
    inner.classList.toggle("hidden", showList)

    if (!group || showList) return

    // If group has subcategories → show subcategory names as chips
    if (group.subcategories) {
      this.addChipLabel(inner, this.labelTryWithValue)
      const chevronHtml = this.chipChevronTemplateTarget.innerHTML
      group.subcategories.forEach((sub, i) => {
        const chip = document.createElement("button")
        chip.type = "button"
        chip.className = "omni-example-chip omni-example-chip--expandable"
        chip.style.animationDelay = `${i * 60}ms`
        chip.dataset.action = "click->omni-search#toggleSubcategoryDropdown"
        chip.dataset.subcategoryIndex = i
        chip.innerHTML = `${this.escapeHtml(sub.name)} ${chevronHtml}`
        inner.appendChild(chip)
      })
      return
    }

    // Flat items → show directly as clickable chips
    const items = group.items || []
    if (items.length) this.addChipLabel(inner, this.labelExampleValue)
    items.forEach((text, i) => {
      const chip = document.createElement("button")
      chip.type = "button"
      chip.className = "omni-example-chip"
      chip.style.animationDelay = `${i * 60}ms`
      chip.dataset.action = "click->omni-search#fillExample"
      chip.dataset.example = text
      chip.textContent = this.truncateExample(text, 65)
      chip.title = text
      inner.appendChild(chip)
    })
  }

  addChipLabel(container, text) {
    const label = document.createElement("span")
    label.className = "text-gray-400 mr-4 select-none"
    label.textContent = text
    container.appendChild(label)
  }

  crossfadeChips() {
    const inner = this.examplesInnerTarget
    inner.style.opacity = "0"
    setTimeout(() => {
      this.renderChips()
      requestAnimationFrame(() => { inner.style.opacity = "1" })
    }, 160)
  }

  // ─── Level 3: Subcategory dropdown (concrete examples) ────

  toggleSubcategoryDropdown(event) {
    event.stopPropagation()
    const index = parseInt(event.currentTarget.dataset.subcategoryIndex, 10)
    const group = this.getActiveGroup()
    const sub = group?.subcategories?.[index]
    if (!sub) return

    // If same subcategory clicked, close
    if (this.exampleDropdownOpen && this.openSubcategoryIndex === index) {
      this.closeExampleDropdown()
      return
    }

    this.openSubcategoryIndex = index

    // Update chip active states
    this.examplesInnerTarget.querySelectorAll(".omni-example-chip--expandable").forEach((chip, i) => {
      chip.classList.toggle("omni-example-chip--expanded", i === index)
    })

    // Populate dropdown
    const container = this.exampleDropdownItemsTarget
    container.innerHTML = ""

    sub.items.forEach(text => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "omni-dropdown-example-item"
      item.dataset.action = "click->omni-search#fillExample"
      item.dataset.example = text
      item.textContent = text
      container.appendChild(item)
    })

    // Position dropdown under the clicked chip
    const chipRect = event.currentTarget.getBoundingClientRect()
    const containerRect = this.examplesContainerTarget.getBoundingClientRect()
    const dropdown = this.exampleDropdownTarget
    const leftOffset = chipRect.left - containerRect.left + (chipRect.width / 2)
    const topOffset = chipRect.bottom - containerRect.top + 8
    dropdown.style.left = `${leftOffset}px`
    dropdown.style.top = `${topOffset}px`
    dropdown.style.transform = "translateX(-50%)"

    dropdown.classList.remove("hidden")
    this.exampleDropdownOpen = true
  }

  closeExampleDropdown() {
    if (!this.exampleDropdownOpen) return
    this.exampleDropdownTarget.classList.add("hidden")
    this.exampleDropdownOpen = false
    this.openSubcategoryIndex = null

    // Remove expanded state from chips
    this.examplesInnerTarget.querySelectorAll(".omni-example-chip--expanded").forEach(chip => {
      chip.classList.remove("omni-example-chip--expanded")
    })
  }

  fillExample(event) {
    const text = event.currentTarget.dataset.example
    this.setInputValue(text)
    this.inputTarget.focus()
    this.inputTarget.setSelectionRange(text.length, text.length)
    this.closeExampleDropdown()
    this.setSpecialistSlug("")
    this.updateSubmitIcon()
  }

  // ─── Typing animation ────────────────────────────────────

  startTypingAnimation() {
    const examples = this.getAllExampleTexts()
    if (!examples.length) return

    this.typingState = {
      examples,
      exampleIndex: 0,
      charIndex: 0,
      isDeleting: false,
      isPaused: false
    }

    this.typingInterval = setInterval(() => this.tickTyping(), 55)
  }

  stopTypingAnimation() {
    if (this.typingInterval) {
      clearInterval(this.typingInterval)
      this.typingInterval = null
    }
    this.typingState = null
  }

  resetTypingAnimation() {
    this.stopTypingAnimation()
    this.inputTarget.placeholder = this.getCurrentMode().placeholder
    this.startTypingAnimation()
  }

  tickTyping() {
    if (!this.typingState) return
    if (this.inputTarget.value.length > 0) return

    const { examples, isDeleting, isPaused } = this.typingState
    if (isPaused) return

    const currentText = examples[this.typingState.exampleIndex % examples.length]

    if (!isDeleting) {
      this.typingState.charIndex++
      if (this.typingState.charIndex > currentText.length) {
        this.typingState.isPaused = true
        setTimeout(() => {
          if (!this.typingState) return
          this.typingState.isPaused = false
          this.typingState.isDeleting = true
        }, 2500)
        return
      }
    } else {
      this.typingState.charIndex -= 2
      if (this.typingState.charIndex <= 0) {
        this.typingState.charIndex = 0
        this.typingState.isDeleting = false
        this.typingState.exampleIndex++
        this.typingState.isPaused = true
        setTimeout(() => {
          if (!this.typingState) return
          this.typingState.isPaused = false
        }, 400)
        return
      }
    }

    this.inputTarget.placeholder = currentText.substring(0, this.typingState.charIndex)
  }

  // ─── Form submission ──────────────────────────────────────

  submit(event) {
    event.preventDefault()
    const query = this.inputTarget.value.trim()
    if (!query) return

    const mode = this.getCurrentMode()

    switch (mode.action_type) {
      case "assistant":
        this.submitAssistant(query)
        break
      case "search":
        this.submitSearch(query, mode.filter_class)
        break
      case "navigate":
        this.submitNavigate(query, mode.path, mode.search_param)
        break
    }
  }

  submitAssistant(query) {
    this.assistantInputTarget.value = query
    if (this.hasModelSelectTarget) {
      this.appendConversationParam("conversation[model]", this.modelSelectTarget.value)
    }
    if (this.hasRetrievalStrategySelectTarget) {
      this.appendConversationParam("conversation[retrieval_strategy]", this.retrievalStrategySelectTarget.value)
    }
    if (this.hasReasoningEffortSelectTarget) {
      this.appendConversationParam("conversation[reasoning_effort]", this.reasoningEffortSelectTarget.value)
    }
    this.appendKnowledgeSources()
    this.assistantFormTarget.requestSubmit()
  }

  // Carry the "+" menu knowledge-sources selection into the hidden submission
  // form, but only when the user actually touched it — an untouched panel keeps
  // the conversation inheriting the org/global default (issue #7641).
  appendKnowledgeSources() {
    const panel = this.element.querySelector('[data-controller~="context-sources"]')
    if (!panel || panel.dataset.contextSourcesTouched !== "true") return

    // Empty entry so an all-unchecked selection persists as [] rather than
    // reverting to the default. appendConversationParam skips blank values, so
    // the marker is appended directly. Only the source checkboxes (target
    // "field") carry a key — the autonomic "select all" parent is excluded.
    this.appendHiddenInput("conversation[enabled_context_keys][]", "")
    panel
      .querySelectorAll('input[type="checkbox"][data-context-sources-target~="field"]:checked')
      .forEach((checkbox) => {
        this.appendHiddenInput("conversation[enabled_context_keys][]", checkbox.value)
      })
  }

  appendHiddenInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    this.assistantFormTarget.appendChild(input)
  }

  appendConversationParam(name, value) {
    if (!value) return
    this.appendHiddenInput(name, value)
  }

  setSpecialistSlug(slug) {
    if (this.hasAssistantSpecialistIdTarget) {
      this.assistantSpecialistIdTarget.value = slug || ""
    }
  }

  submitSearch(query, filterClass) {
    const url = `${this.searchPathValue}?search[title]=${encodeURIComponent(query)}&search[filter_class]=${encodeURIComponent(filterClass)}`
    Turbo.visit(url)
  }

  submitNavigate(query, basePath, searchParam) {
    const param = searchParam || "search[title]"
    const separator = basePath.includes("?") ? "&" : "?"
    const url = `${basePath}${separator}${param}=${encodeURIComponent(query)}`
    Turbo.visit(url)
  }

  // ─── Input handling ───────────────────────────────────────

  onInput() {
    this.updateSubmitIcon()
  }

  setInputValue(text) {
    this.inputTarget.value = text
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    // Trigger autogrow directly in case the event listener hasn't connected
    const autogrowCtrl = this.application.getControllerForElementAndIdentifier(this.inputTarget, "autogrow")
    if (autogrowCtrl) autogrowCtrl.autogrow()
  }

  updateSubmitIcon() {
    if (!this.hasSubmitIconTarget) return
    const hasValue = this.inputTarget.value.trim().length > 0
    this.submitIconTarget.classList.toggle("opacity-40", !hasValue)
    this.submitIconTarget.classList.toggle("opacity-100", hasValue)
  }

  onKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      // Don't submit if the slash command menu is open — let the slash-command
      // controller handle Enter to select the highlighted specialist
      const slashMenu = this.element.querySelector(".slash-command-menu--open")
      if (slashMenu) return

      event.preventDefault()
      this.inputTarget.closest("form").requestSubmit()
      return
    }
    if (event.key === "Escape") {
      if (this.exampleDropdownOpen) {
        this.closeExampleDropdown()
        return
      }
      this.setInputValue("")
      this.inputTarget.blur()
      this.updateSubmitIcon()
    }
  }

  // ─── Slash command integration ───────────────────────

  updateSlashCommandState(mode) {
    const isAssistant = mode.action_type === "assistant"
    this.element.dataset.slashCommandEnabledValue = isAssistant

    // Clear any selected specialist when leaving assistant mode
    if (!isAssistant && this.hasAssistantSpecialistIdTarget) {
      this.assistantSpecialistIdTarget.value = ""
      const pill = this.element.querySelector("[data-slash-command-target='pill']")
      if (pill) {
        pill.innerHTML = ""
        pill.classList.add("hidden")
        pill.classList.remove("flex")
      }
    }
  }

  // ─── Data helpers ─────────────────────────────────────────

  getCurrentMode() {
    return this.modesValue.find(m => m.id === this.currentModeValue) || this.modesValue[0]
  }

  getActiveGroup() {
    const mode = this.getCurrentMode()
    const groups = mode?.examples || []
    return groups[this.activeGroupIndex] || groups[0]
  }

  getAllExampleTexts() {
    const mode = this.getCurrentMode()
    if (!mode?.examples?.length) return [mode?.placeholder || ""]

    return mode.examples.flatMap(group => {
      if (group.specialists) {
        return (group.items || []).map(s => s.sample_question)
      }
      if (group.subcategories) {
        return group.subcategories.flatMap(sub => sub.items)
      }
      return group.items || []
    })
  }

  truncateExample(text, maxLength) {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength - 1) + "…"
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
